import {useEffect} from 'react';
import moment, {MomentInput} from 'moment';
import {SelectGroupOption} from '../../../components/AutoForm/AutoFieldNew';
import FREQUENCY, {toFrequency} from '../../../constants/Frequency';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {withdrawalFieldsType} from '../BuilderWithdrawal/builderWithdrawalFields';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import loadMomentDate from '../../../utils/loadMomentDate';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {getWithdrawalMinimumDate} from '../BuilderWithdrawal/builderGetWithdrawalMinimumDate';
import {FULL_SURRENDER} from '../BuilderWithdrawal/builderWithdrawalFormFields';
import {includesSaltus} from '../../../utils/includesSaltus';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {UPDATES} from '../../../shared/UPDATES';
import {findParentOfChild} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {
  useAutoForm,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';

// ============================================
// BuilderWithdrawalHelpers.tsx (3404-3407)
// ============================================

// Shared defaults for `withdraw.source` — used by the change factory and the load path.
export const changeWithdrawalSource = ({
  pot,
  parentPotId,
  carryOver,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
  parentPotId?: string;
  carryOver?: withdrawalFieldsType['source'];
}): {
  source: withdrawalFieldsType['source'];
  pot: BuilderPot | BuilderPot_dentistChildren;
} => {
  return {
    source: {
      frequency: FREQUENCY.SINGLE,
      ...carryOver,
      id: pot.id,
      parentPotId,
      singleClientAmount: 0,
      maxAmount: getCurrencyFloat(pot.totalValue),
      isSaltus: includesSaltus(pot.productProvider?.formattedName),
      crystallisedFunds: pot.crystallisedFunds,
      uncrystallisedFunds: pot.uncrystallisedFunds,
    },
    pot,
  };
};

export const changeWithdrawalSourceFactory = ({
  sourcePots,
  withdraw,
  withdrawSourceHelpers,
}: {
  sourcePots: BuilderPot[];
  withdraw: withdrawalFieldsType | undefined;
  withdrawSourceHelpers: AutoFieldHelpers<withdrawalFieldsType['source']>;
}) => {
  return (value: string) => {
    const parent = findParentOfChild(sourcePots, value);
    if (parent) {
      const child = parent.dentistChildren?.find(
        (c: BuilderPot_dentistChildren) => c.id === value
      );
      if (!child) return;
      const {source} = changeWithdrawalSource({
        pot: child,
        parentPotId: parent.id,
        carryOver: withdraw?.source,
      });
      withdrawSourceHelpers.setValue(UPDATES._D3404, source);
      return;
    }
    // Leaf pot (no dentist children) — use the pot directly
    const leafPot = sourcePots.find((p) => p.id === value);
    if (!leafPot) return;
    const {source} = changeWithdrawalSource({
      pot: leafPot,
      parentPotId: undefined,
      carryOver: withdraw?.source,
    });
    withdrawSourceHelpers.setValue(UPDATES._D3404, source);
  };
};

// Destinations are BankAccount entities (DBO-1436) — selection is just the id.
export const changeWithdrawalDestinationFactory = ({
  withdrawDestinationHelpers,
}: {
  withdrawDestinationHelpers: AutoFieldHelpers<withdrawalFieldsType['destination']>;
}) => {
  return (bankAccountId: string) => {
    withdrawDestinationHelpers.setValue(UPDATES._D3405, {id: bankAccountId});
  };
};

// Keep a choice still on offer, otherwise pick the only option, otherwise leave it to the adviser.
export const nextWithdrawalDestination = (
  destinationAccounts: {id: string}[],
  selectedId: string | undefined
): string | undefined => {
  if (selectedId && destinationAccounts.some((account) => account.id === selectedId)) {
    return selectedId;
  }
  return destinationAccounts.length === 1 ? destinationAccounts[0].id : undefined;
};

export const useSyncWithdrawalDestination = ({
  destinationAccounts,
  areDestinationsLoaded,
  selectedId,
  withdrawDestinationHelpers,
}: {
  destinationAccounts: {id: string}[];
  areDestinationsLoaded: boolean;
  selectedId: string | undefined;
  withdrawDestinationHelpers: AutoFieldHelpers<
    withdrawalFieldsType['destination'] | undefined
  >;
}) => {
  useEffect(() => {
    if (!areDestinationsLoaded) return;
    const nextId = nextWithdrawalDestination(destinationAccounts, selectedId);
    if (nextId === selectedId) return;
    withdrawDestinationHelpers.setValue(
      UPDATES._D3405,
      nextId ? {id: nextId} : undefined
    );
  }, [areDestinationsLoaded, destinationAccounts, selectedId]);
};

export const loadSavedWithdrawal = ({
  builderDetails,
  withdrawHelpers,
}: {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  withdrawHelpers: AutoFieldHelpers<withdrawalFieldsType | undefined>;
}) => {
  if (!builderDetails) {
    return;
  }

  const savedWithdrawal = getSavedWithdrawalValues(builderDetails);
  if (!savedWithdrawal) return;
  withdrawHelpers.setValue(UPDATES._D3407, {
    ...savedWithdrawal,
  });
};

// ============================================
// BuilderWithdrawalSources.tsx (300-303)
// ============================================

export const handleWithdrawalFullAndReason = ({
  withdrawalValues,
  values,
  options,
  setFieldValue,
  setFullAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: builderFieldsType;
  options: SelectGroupOption[];
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  setFullAmount: () => void;
}) => {
  if (withdrawalValues.source.full) {
    setFieldValue(UPDATES._D300, 'withdraw.source.reason', FULL_SURRENDER);
    setFieldValue(UPDATES._D301, 'withdraw.source.frequency', FREQUENCY.SINGLE);
    setFullAmount();
    return;
  }
  if (options.length && options[0]) {
    if (!values.withdraw?.source.reason) {
      setFieldValue(UPDATES._D303, 'withdraw.source.reason', null);
    }
    const reasonIsInOptions = options.find((x) =>
      x.options.find((y) => y.value === values.withdraw?.source.reason)
    );
    if (!reasonIsInOptions) {
      setFieldValue(UPDATES._D303, 'withdraw.source.reason', null);
    }
  }
};

export const useHandleWithdrawalFullAndReason = ({
  options,
  setFullAmount,
}: {
  options: SelectGroupOption[];
  setFullAmount: () => void;
}) => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const withdrawalValues = values.withdraw!;
  useEffect(() => {
    handleWithdrawalFullAndReason({
      withdrawalValues,
      values,
      options,
      setFieldValue,
      setFullAmount,
    });
  }, [options, values.withdraw?.source.reason, withdrawalValues.source.full]);
};

// ============================================
// BuilderWithdrawalSourcesHelpers.tsx (304-314)
// ============================================

export const setWithdrawalFullAmountFactory = ({
  withdrawalValues,
  totalAmount,
  setAmount,
}: {
  withdrawalValues: withdrawalFieldsType;
  totalAmount: number | undefined;
  setAmount: AutoFieldHelpers<moneyFieldValueType>['setValue'];
}) => {
  return () => {
    if (withdrawalValues.source?.full) {
      setAmount(UPDATES._D306, totalAmount || '');
      withdrawalValues.source.singleClientAmount = totalAmount || '';
    } else {
      setAmount(UPDATES._D307, withdrawalValues.source?.singleClientAmount);
    }
  };
};

export const setStartDateMustBe10daysInTheFuture = ({
  withdrawalValues,
  values,
  setFieldValue,
  today, // need to pass this in for testing
  builderDetails,
}: {
  withdrawalValues: withdrawalFieldsType;
  values: builderFieldsType;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  today: MomentInput;
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
}) => {
  if (
    withdrawalValues.source?.start ||
    values.withdraw?.source?.frequency === FREQUENCY.SINGLE
  ) {
    return;
  }
  const currentOrSavedDate = withdrawalValues.source?.start || builderDetails?.startDate;
  const minimumDate = getWithdrawalMinimumDate(today, moment(currentOrSavedDate));

  setFieldValue(
    UPDATES._D304,
    'withdraw.source.start',
    // withdrawal start date can only be at least 10 days in the future
    minimumDate,
    true
  );
};

export const getSavedWithdrawalValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): Omit<withdrawalFieldsType, 'planProvider'> | undefined => {
  if (!builderDetails?.withdraw) return undefined;
  const savedSource = builderDetails?.potPlans?.find((p) => p.planType === 'source');

  const frequency = toFrequency(builderDetails?.potPlans?.[0]?.recurringClientFrequency);

  const source: withdrawalFieldsType['source'] = {
    frequency,
    singleClientAmount: getCurrencyFloat(savedSource?.singleClientAmount) || '',
    id: savedSource?.pot.id,
    parentPotId: savedSource?.pot.parent?.id ?? undefined,
    reason:
      savedSource?.pot.withdrawalReason || builderDetails?.withdrawalReason || undefined,
    recurringClientAmount: getCurrencyFloat(savedSource?.recurringClientAmount),
    maxAmount: savedSource?.pot.totalValue,
    full: savedSource?.full || false,
    isSaltus: includesSaltus(savedSource?.pot.productProvider?.formattedName),
    crystallisedFunds: savedSource?.pot.crystallisedFunds,
    uncrystallisedFunds: savedSource?.pot.uncrystallisedFunds,
    start: loadMomentDate(
      builderDetails?.startDate ? String(builderDetails?.startDate) : undefined
    ),
  };

  const initialFee = savedSource?.initialFee?.id;

  // The saved destination is a BankAccount (or a pot on legacy recommendations);
  // either way the union member carries the id used to reselect from the list.
  const destinationId = builderDetails.withdraw?.destination?.id;
  const destination: withdrawalFieldsType['destination'] | undefined = destinationId
    ? {id: destinationId}
    : undefined;

  return {source, initialFee, ...(destination ? {destination} : {})};
};
